class EngravingCartDrawerItemActions extends HTMLElement {
  constructor() {
    super();
    this.cart = document.querySelector("cart-drawer");
    this.engravingData = this.getEngravingData();
    this.currentProductEngravingOptions = [];
    this.ringEngravingOptions = [];
    this.watchEngravingOptions = [];
    this.pendantEngravingOptions = [];
    this.engravingProperties = [
      "_Line1",
      "_Line2",
      "_Line3",
      "_engraving_service_price",
      "_engraving_service_variant_id",
      "_engraving_type",
      "_engraving_mode",
      "_engraving_customization_option",
      "_engraving_font",
      "_engraving_font_ttf",
      "_engraving_template_id",
      "_engraving_icon_url",
      "Engraving",
    ];
    this.productProperties = this.getProductProperties();
  }

  connectedCallback() {
    this.loadEngravingBasedOnProductType();
    this.loadProductEngravingOptions();
    this.cacheElements();
    this.attachEventListeners();

    const gate = window.OGEngravingExperiment;
    if (gate?.getResult?.()) {
      this.updateEngravingAvailability();
    } else if (gate?.whenReady) {
      this.dataset.showEngravingActions = false;
      gate.whenReady(() => this.updateEngravingAvailability());
    } else {
      this.updateEngravingAvailability();
    }
  }

  disconnectedCallback() {
    this.addEngraving?.removeEventListener(
      "click",
      this.handleAddEngravingClick.bind(this)
    );
    this.editEngraving?.removeEventListener(
      "click",
      this.handleEditEngravingClick.bind(this)
    );
    this.removeEngraving?.removeEventListener(
      "click",
      this.handleRemoveEngravingClick.bind(this)
    );
  }

  /** Caches the action elements rendered for this cart line. */
  cacheElements() {
    this.addEngraving = this.querySelector(
      ".cart-drawer-item--engraving-action-add"
    );

    this.editEngraving = this.querySelector(
      ".cart-drawer-item--engraving-action-edit"
    );

    this.removeEngraving = this.querySelector(
      ".cart-drawer-item--engraving-action-remove"
    );

    this.engravingLines = this.querySelector("ul");
  }

  /** Updates whether this cart line can render engraving actions. */
  updateEngravingAvailability() {
    const hasExistingEngraving = Boolean(this.dataset.engravingType);
    const hasAvailableEngraving = this.isTreatmentExperience()
      ? this.hasTreatmentEngraving()
      : this.getLegacyEngravingOptions().length > 0;

    if (
      this.dataset.engravingType === "free_engraving" ||
      hasExistingEngraving ||
      hasAvailableEngraving
    ) {
      this.dataset.showEngravingActions = true;
    } else {
      this.dataset.showEngravingActions = false;
    }
  }

  /** Attaches event listeners for engraving actions. */
  attachEventListeners() {
    this.addEngraving?.addEventListener(
      "click",
      this.handleAddEngravingClick.bind(this)
    );
    this.editEngraving?.addEventListener(
      "click",
      this.handleEditEngravingClick.bind(this)
    );
    this.removeEngraving?.addEventListener(
      "click",
      this.handleRemoveEngravingClick.bind(this)
    );
  }

  /** Loads engraving options based on product type. */
  loadEngravingBasedOnProductType() {
    const { itemType } = this.dataset;

    if (itemType.includes("watch")) {
      this.watchEngravingOptions = this.engravingData.filter((el) =>
        ["caseback", "top-arch-caseback", "sidewall"].includes(el.engravingType)
      );
      return;
    }

    if (itemType.includes("ring")) {
      this.ringEngravingOptions = this.engravingData.filter(
        (el) => el.engravingType === "ring"
      );
      return;
    }

    if (itemType.includes("pendant") || itemType.includes("necklace")) {
      this.pendantEngravingOptions = this.engravingData.filter(
        (el) => el.engravingType === "pendant"
      );
      return;
    }
  }

  /** Loads engraving options specific to the current product. */
  loadProductEngravingOptions() {
    const { itemType, itemHandle } = this.dataset;
    if (itemType.includes("watch")) {
      this.currentProductEngravingOptions = this.watchEngravingOptions.filter(
        (el) => el.associatedProducts.includes(itemHandle)
      );
      return;
    }
    if (itemType.includes("ring")) {
      this.currentProductEngravingOptions = this.ringEngravingOptions.filter(
        (el) => el.associatedProducts.includes(itemHandle)
      );
      return;
    }
    if (itemType.includes("pendant") || itemType.includes("necklace")) {
      this.currentProductEngravingOptions = this.pendantEngravingOptions.filter(
        (el) => el.associatedProducts.includes(itemHandle)
      );
      return;
    }
  }

  /** Sidewall is unavailable for new selections in either experiment group. */
  getLegacyEngravingOptions() {
    return this.currentProductEngravingOptions.filter(
      (option) => option.engravingType !== "sidewall"
    );
  }

  /**
   * Handles the add engraving button click event.
   * @param {Event} event - The click event.
   */
  async handleAddEngravingClick(event) {
    const btn = event.target.closest(".cart-drawer-item--engraving-action-add");
    if (!btn) return;
    const currentProductEngravingOptions = this.getLegacyEngravingOptions();
    if (!currentProductEngravingOptions.length) return;
    await this.withCurrentProperties(btn, () => {
      const addEngravingEvent = new CustomEvent("cartItemAddEngraving", {
        detail: {
          useTreatmentEngraving: this.isTreatmentExperience(),
          currentProductEngravingOptions,
          productVariantId: this.dataset.itemId,
          lineItemKey: this.dataset.itemKey,
          productProperties: this.getCleanProductProperties(),
        },
      });
      document.dispatchEvent(addEngravingEvent);
      this.cart.close();
    });
  }

  /**
   * Handles the edit engraving button click event.
   * @param {Event} event - The click event.
   */

  async handleEditEngravingClick(event) {
    const btn = event.target.closest(
      ".cart-drawer-item--engraving-action-edit"
    );
    if (!btn) return;
    // Preserve saved Sidewall customization without reopening a retired service.
    if (this.dataset.engravingType === "sidewall") return;
    const currentProductEngravingOptions = this.getLegacyEngravingOptions();
    if (!currentProductEngravingOptions.length) return;
    await this.withCurrentProperties(btn, () => {
      const engravingData = this.getItemEngravingLines();
      const editEngravingEvent = new CustomEvent("cartItemEditEngraving", {
        detail: {
          useTreatmentEngraving: this.isTreatmentExperience(),
          engravingData,
          productVariantId: this.dataset.itemId,
          lineItemKey: this.dataset.itemKey,
          currentProductEngravingOptions,
          productProperties: this.getCleanProductProperties(),
          engravingProperties: this.productProperties,
        },
      });
      document.dispatchEvent(editEngravingEvent);
      this.cart.close();
    });
  }

  /**
   * Handles the remove engraving button click event.
   * @param {Event} event - The click event.
   */
  async handleRemoveEngravingClick(event) {
    const btn = event.target.closest(
      ".cart-drawer-item--engraving-action-remove"
    );
    if (!btn) return;
    await this.withCurrentProperties(btn, () => this.removeEngravingProperties());
  }

  async withCurrentProperties(button, action) {
    if (this.isLoadingProperties) return;
    this.isLoadingProperties = true;
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    this.querySelector("[data-engraving-load-status]")?.remove();
    try {
      // Liquid line properties can contain HTML entities. Use the original
      // cart values so editing never encodes punctuation or decodes literal text.
      const response = await fetch(window.Shopify.routes.root + "cart.js", { cache: "no-store" });
      if (!response.ok) throw new Error("The cart could not be loaded.");
      const cart = await response.json();
      const item = cart.items.find((line) => line.key === this.dataset.itemKey);
      if (!item) throw new Error("This cart item has changed.");
      this.productProperties = item.properties || {};
      await action();
    } catch (error) {
      console.error("Loading engraving details failed:", error);
      const status = document.createElement("p");
      status.dataset.engravingLoadStatus = "";
      status.className = "engraving-critical-msg";
      status.setAttribute("role", "alert");
      status.textContent = "We couldn't load the engraving details. Please refresh the cart and try again.";
      button.insertAdjacentElement("afterend", status);
    } finally {
      this.isLoadingProperties = false;
      button.disabled = false;
      button.removeAttribute("aria-busy");
    }
  }

  async removeEngravingProperties() {
    let newProperties = this.getCleanProductProperties();
    let cartData = {
      id: this.dataset.itemKey || this.dataset.itemId,
      properties: {
        ...newProperties,
      },
      sections:
        this.cart?.getSectionsToRender?.().map((section) => section.id) || [],
      sections_url: window.location.pathname,
    };
    this.setRemovePending(true);
    this.showRemoveMessage("");

    try {
      const request = await fetch(
        window.Shopify.routes.root + "cart/change.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(cartData),
        }
      );
      const response = await request.json().catch(() => null);
      if (!request.ok) {
        throw new Error(
          response?.description || `cart/change.js ${request.status}`
        );
      }
      this.renderCartDrawer(response);
    } catch (error) {
      console.error("Removing engraving failed:", error);
      this.showRemoveMessage(
        "We couldn't remove the engraving. Please try again."
      );
    } finally {
      this.setRemovePending(false);
    }
  }

  setRemovePending(isPending) {
    if (!this.removeEngraving) return;
    this.removeEngraving.disabled = isPending;
    this.removeEngraving.setAttribute(
      "aria-busy",
      isPending ? "true" : "false"
    );
  }

  showRemoveMessage(message) {
    if (!this.removeEngraving) return;

    let status = this.querySelector("[data-engraving-remove-status]");
    if (!message) {
      status?.remove();
      return;
    }

    if (!status) {
      status = document.createElement("p");
      status.dataset.engravingRemoveStatus = "";
      status.className = "engraving-critical-msg";
      status.setAttribute("role", "alert");
      this.removeEngraving.insertAdjacentElement("afterend", status);
    }
    status.textContent = message;
  }

  getCleanProductProperties() {
    let newProperties = { ...this.productProperties };
    this.engravingProperties.forEach((item) => {
      delete newProperties[item];
    });

    return !Object.keys(newProperties).length
      ? { _engraving_removed: true }
      : newProperties;
  }

  getItemEngravingLines() {
    return {
      engravingType:
        this.productProperties._engraving_type ||
        this.engravingLines?.dataset.engravingType ||
        "",
      engravingLines: [
        this.productProperties._Line1 || "",
        this.productProperties._Line2 || "",
        this.productProperties._Line3 || "",
      ],
    };
  }

  /**
   * Retrieves engraving data from the DOM.
   * @returns {Object} Parsed engraving data.
   */
  getEngravingData() {
    return JSON.parse(
      document.querySelector("#engraving-options-data").textContent
    ).engravingOptions;
  }

  isTreatmentExperience() {
    const result = window.OGEngravingExperiment?.getResult?.();
    return (
      result?.assignedTreatment === true && this.hasTreatmentEngraving()
    );
  }

  hasTreatmentEngraving() {
    return Boolean(window.OGEngravingExperiment?.getTreatmentOption({ variantId: this.dataset.itemId }));
  }

  /** Renders the cart drawer with the updated cart response.
   * @param {Object} response - The cart response object. */
  renderCartDrawer(response) {
    const { sections } = response;

    const parsedResponse = {
      id: response.id,
      sections,
    };

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: "engraving-form",
      productVariantId: parsedResponse.id,
      cartData: parsedResponse,
    });

    this.cart.renderContents(parsedResponse);

    if (this.cart && this.cart.classList.contains("is-empty"))
      this.cart.classList.remove("is-empty");
  }

  getProductProperties() {
    return JSON.parse(
      this.querySelector(`script[type="application/json"]`).textContent
    );
  }
}
customElements.define(
  "cart-drawer-item-engraving-actions",
  EngravingCartDrawerItemActions
);
